import {
  ChangeEventHandler,
  FocusEventHandler,
  InputHTMLAttributes,
  MouseEventHandler,
  Suspense,
  lazy,
  useCallback,
  useState,
} from 'react';
import styles from './styles.module.css';
import classNames from 'classnames';
import Popup, { usePopupCloser } from '../Popup';
import { format, parse } from 'date-fns';
import { ja } from 'date-fns/locale';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import { Placement } from '@floating-ui/react-dom';

const Calendar = lazy(() => import('./Calendar'));

/** 日付表示のフォーマット (date-fns 形式) */
const dayTextFormat = 'yyyy/M/d';
/** (直近の) 日付表示のフォーマット (date-fns 形式) */
const recentlyDayTextFormat = 'M/d(E)';

type Variant = 'error' | 'recently';

type Props = {
  variants?: Variant[];
  placement?: Placement;
  value: Date | undefined;
  onChange?: (value: Date | undefined) => void;
} & Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'placeholder' | 'disabled' | 'onBlur'
>;

function DateInput({
  variants = [],
  placement = 'bottom-start',
  value,
  onChange,
  onBlur: onBlurRaw,
  ...props
}: Props) {
  const { input, onInput, onBlur } = useDayText(value, onChange, onBlurRaw);
  const { containerRef, isCalendarOpen, toggleCalendarOpen, closeCalendar } =
    useCalendarState();

  return (
    <div className={styles.container} ref={containerRef}>
      {variants.includes('recently') ? (
        <RecentlyCover value={value} onClick={toggleCalendarOpen} />
      ) : (
        <>
          <input
            className={classNames(
              styles.input,
              ...variants.map((v) => styles[v])
            )}
            value={input}
            onChange={onInput}
            onClick={toggleCalendarOpen}
            onBlur={onBlur}
            {...props}
          />
          <CalendarButton onClick={toggleCalendarOpen} />
        </>
      )}
      {isCalendarOpen && containerRef.current && (
        <CalendarPopup
          container={containerRef.current}
          placement={placement}
          selected={value}
          onSelect={(v) => {
            closeCalendar();
            onChange?.(v);
          }}
        />
      )}
    </div>
  );
}

export default DateInput;

function CalendarButton({ onClick }: { onClick?: MouseEventHandler }) {
  return (
    <button type="button" className={styles.calendarIcon} onClick={onClick}>
      <FontAwesomeIcon icon={faCalendarDays} />
    </button>
  );
}

function RecentlyCover({
  value = new Date(),
  onClick,
}: {
  value?: Date;
  onClick?: MouseEventHandler;
}) {
  return (
    <div className={styles.recentlyCover} onClick={onClick}>
      {value && format(value, recentlyDayTextFormat, { locale: ja })}
    </div>
  );
}

function CalendarPopup({
  container,
  placement,
  selected,
  onSelect,
}: {
  container: HTMLElement;
  placement?: Placement;
  selected: Date | undefined;
  onSelect?: (day: Date | undefined) => void;
}) {
  return (
    <Popup reference={container} placement={placement}>
      <Suspense>
        <Calendar selected={selected} onSelect={onSelect} />
      </Suspense>
    </Popup>
  );
}

function useDayText(
  value: Date | undefined,
  onChange: ((day: Date | undefined) => void) | undefined,
  onBlurRaw: Props['onBlur']
) {
  const [input, setInput] = useState<string>();
  const dayText = value ? format(value, dayTextFormat) : '';

  const onInput = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const v = e.currentTarget.value;
      setInput(v);
      if (v) {
        const day = parse(v, dayTextFormat, new Date());
        if (!Number.isNaN(day.getTime())) onChange?.(day);
      } else {
        onChange?.(undefined);
      }
    },
    [onChange]
  );

  const onBlur: FocusEventHandler<HTMLInputElement> = useCallback((event) => {
    setInput(undefined);
    onBlurRaw?.(event);
  }, []);

  return { input: input ?? dayText, onInput, onBlur };
}

function useCalendarState() {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const toggleCalendarOpen = useCallback(() => {
    setIsCalendarOpen((s) => !s);
  }, []);

  const closeCalendar = useCallback(() => {
    setIsCalendarOpen(false);
  }, []);

  const { containerRef } = usePopupCloser(closeCalendar);

  return { containerRef, isCalendarOpen, toggleCalendarOpen, closeCalendar };
}
